import { GameState } from './chess-engine';
import { getBotMove } from './bot-ai';
import { getStockfishMove, type Move } from './engine-adapter';
import { setLastEngineUsed, addDiagnosticError } from './engine-diagnostics';

export type EngineType = 'native' | 'stockfish';

export interface EngineConfig {
  preferredEngine: EngineType;
  fallbackEnabled: boolean;
  stockfishTimeout?: number;
}

export interface EngineResult {
  move: Move | null;
  engine: EngineType;
  error?: string;
}

/**
 * Default engine configuration - prefers Stockfish with native fallback
 */
const DEFAULT_CONFIG: EngineConfig = {
  preferredEngine: 'stockfish',
  fallbackEnabled: true,
  stockfishTimeout: 10000
};

/**
 * Gets the best move using the unified engine interface
 * Automatically handles fallback from Stockfish to native engine if needed
 */
export async function getEngineMove(
  gameState: GameState, 
  botElo: number, 
  config: EngineConfig = DEFAULT_CONFIG,
  moveNumber?: number
): Promise<EngineResult> {
  // Handle alternating engines for intermediate bots (700-900 ELO)
  let finalConfig = config;
  if (botElo >= 700 && botElo <= 900 && moveNumber !== undefined) {
    // Alternate between engines based on move number
    const useStockfish = moveNumber % 2 === 0;
    finalConfig = {
      preferredEngine: useStockfish ? 'stockfish' : 'native',
      fallbackEnabled: true, // Enable fallback for robustness
      stockfishTimeout: config.stockfishTimeout
    };
    console.log(`🔄 Alternating Engine for ELO ${botElo}, Move ${moveNumber}: ${useStockfish ? 'Stockfish' : 'Native'}`);
  } else if (botElo <= 600) {
    // Route very low ELO bots to native engine for more realistic human-like play
    finalConfig = { preferredEngine: 'native' as const, fallbackEnabled: false, stockfishTimeout: config.stockfishTimeout };
    console.log(`🎯 Routing ELO ${botElo} to Native Engine (more realistic for beginners)`);
  }
    
  console.log(`🎯 Engine Selection for ELO ${botElo}: ${finalConfig.preferredEngine === 'native' ? 'Native Bot AI' : 'Stockfish'}`);
    
  // Try preferred engine first
  if (finalConfig.preferredEngine === 'stockfish') {
    try {
      console.log(`[Engine] Trying Stockfish for ELO ${botElo}`);
      const move = await getStockfishMove(gameState, botElo);
      
      if (move) {
        console.log(`[Engine] Stockfish success:`, move);
        setLastEngineUsed('stockfish');
        return { move, engine: 'stockfish' };
      } else {
        console.warn('[Engine] Stockfish returned null move');
        if (!config.fallbackEnabled) {
          return { move: null, engine: 'stockfish', error: 'Stockfish returned no move' };
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Stockfish error';
      console.error('[Engine] Stockfish failed:', error);
      addDiagnosticError(`Stockfish failed: ${errorMsg}`);
      
      // Check if it's a circuit breaker error
      if (errorMsg.includes('Circuit breaker is OPEN')) {
        console.warn('[Engine] Circuit breaker is open, forcing fallback to native');
      }
      
      if (!config.fallbackEnabled) {
        return { 
          move: null, 
          engine: 'stockfish', 
          error: errorMsg 
        };
      }
    }
    
    // Fallback to native if enabled
    if (config.fallbackEnabled) {
      console.log('[Engine] Falling back to native engine after Stockfish failure');
      try {
        const move = await getBotMove(gameState, botElo);
        setLastEngineUsed('native');
        return { move, engine: 'native' };
      } catch (error) {
        console.error('[Engine] Native engine also failed:', error);
        return { 
          move: null, 
          engine: 'native', 
          error: error instanceof Error ? error.message : 'Native engine error' 
        };
      }
    }
  } else {
    // Use native engine directly
    try {
      console.log(`[Engine] Using native engine for ELO ${botElo}`);
      const move = await getBotMove(gameState, botElo);
      setLastEngineUsed('native');
      return { move, engine: 'native' };
    } catch (error) {
      console.error('[Engine] Native engine failed:', error);
      return { 
        move: null, 
        engine: 'native', 
        error: error instanceof Error ? error.message : 'Native engine error' 
      };
    }
  }
  
  return { move: null, engine: config.preferredEngine, error: 'All engines failed' };
}

/**
 * Gets the best move with Stockfish preference (convenience function)
 */
export async function getStockfishPreferredMove(
  gameState: GameState, 
  botElo: number
): Promise<Move | null> {
  const result = await getEngineMove(gameState, botElo, {
    preferredEngine: 'stockfish',
    fallbackEnabled: true
  });
  
  return result.move;
}

/**
 * Gets the best move with native engine only (convenience function)
 */
export async function getNativeMove(
  gameState: GameState, 
  botElo: number
): Promise<Move | null> {
  const result = await getEngineMove(gameState, botElo, {
    preferredEngine: 'native',
    fallbackEnabled: false
  });
  
  return result.move;
}

/**
 * Gets available engine types
 */
export function getAvailableEngines(): EngineType[] {
  return ['stockfish', 'native'];
}

/**
 * Checks if Stockfish is available (basic check)
 */
export function isStockfishAvailable(): boolean {
  try {
    // Basic check for Worker support and Stockfish files
    return typeof Worker !== 'undefined' && typeof window !== 'undefined';
  } catch {
    return false;
  }
}